// @flow

import React, {useState, useEffect, useRef} from "react";
import {Col, Table, Form, Input, Switch, Button, Checkbox, Pagination, Spin} from "antd";
import {useFormik, FormikProvider, getIn} from "formik";
import {useHistory} from "react-router-dom";
import {useTranslation} from "react-i18next";

import {handleCheckBoxText} from "../../../utils/helpers";
import {ErrorFocus} from "../../_defaultComponents/ErrorFocus/ErrorFocus";
import {
    CompanyContanier,
    CompanyButtonContainer,
    SubCompanyContainer,
    FilterContainer,
    MergeOptionsWrapper,
    FilterWrapper,
    MergeOptionsTableWrap,
    MergeOptionsList,
    MergeOptionsListRow,
    SubCompanyTableWrap,
    SubCompanyCardList,
    SubCompanyCard,
    SubCompanyCardTitle,
    SubCompanyCardRow,
    SubCompanyCardActions
} from "./optionsStyles";
import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {CityFilter} from "../../_defaultComponents/FilterComponents";
import {EmptyMessage} from "../../_defaultComponents/EmptyMessage/EmptyMessage";

const {Column} = Table;

export const DynamicPriceOptions = ({companyId}) => {
    const stores = useStore();
    const history = useHistory();
    const {t} = useTranslation(["dynamicPriceOptions"]);

    const [currentPage, setCurrentPage] = useState(1);
    const [isCompanyFormChanged, setIsCompanyFormChanged] = useState(false);
    const [companyFormData, setCompanyFormData] = useState();
    const [city, setCity] = useState("all");
    const [searchText, setSearchText] = useState("");
    const [text, setText] = useState("");
    const [showSubmitButton, setShowSubmitButton] = useState({});
    const [isSubCompanySubmitting, setIsSubCompanySubmitting] = useState(false);
    const [companyRecall, setCompanyRecall] = useState(false);
    const [subCompanyRecall, setSubCompanyRecall] = useState(false);

    const companyRecallRef = useRef(companyRecall);
    companyRecallRef.current = companyRecall;
    const subCompanyRecallRef = useRef(subCompanyRecall);
    subCompanyRecallRef.current = subCompanyRecall;

    const [companyLoading, companyData, setCompanyData, setCompanyLoading] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.companyStore.getCompany.bind(null, {
            companyId,
            withSubCompanies: false,
            vehicleType: null
        }),
        dependencies: [companyRecall]
    });

    const [subCompanyLoading, subCompanyData, setSubCompanyData, setSubCompanyLoading] = useFetch({
        defaultDataValue: {},
        fetchFn:
            companyData.data && companyData.data._id
                ? stores.companyStore.getSubCompanyList.bind(null, {
                      companyId: companyData.data._id,
                      limit: 10,
                      skip: (currentPage - 1) * 10,
                      activity: "enabled",
                      vehicleType: null,
                      city,
                      searchText
                  })
                : () => [],
        dependencies: [companyData, currentPage, subCompanyRecall, city, searchText]
    });

    const formik = useFormik({
        initialValues: {
            options: {
                dynamicPrice: {
                    isActive: companyData?.data?.options?.dynamicPrice?.isActive,
                    firstPrice: companyData?.data?.options?.dynamicPrice?.firstPrice,
                    minimumPrice: companyData?.data?.options?.dynamicPrice?.minimumPrice,
                    discountRate: companyData?.data?.options?.dynamicPrice?.discountRate,
                    applyDynamicPriceOptions: false
                }
            },
            ...(companyData && companyData.data)
        },
        enableReinitialize: true,
        validateOnChange: true,
        onSubmit: async values => {
            setCompanyLoading(true);

            let filters = {};
            filters.searchText = searchText;
            filters.city = city;
            if (Object.keys(filters).length > 0) {
                values.filters = filters;
            }
            await stores.companyStore.updateCompanyDynamicPriceOptions(values);
            if (values.isActive) {
                setSubCompanyRecall(!subCompanyRecallRef.current);
            }
            setCompanyRecall(!companyRecallRef.current);
            setCompanyLoading(false);
            setIsCompanyFormChanged(false);
        }
    });

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue
    } = formik;

    useEffect(() => {
        if (values && !isCompanyFormChanged) {
            setCompanyFormData(values);
        } else if (values && JSON.stringify(companyFormData) !== JSON.stringify(values)) {
            setIsCompanyFormChanged(true);
        } else {
            setIsCompanyFormChanged(false);
        }
    }, [values]);

    const onSubCompanySubmit = async index => {
        setIsSubCompanySubmitting(true);
        let submitData = subCompanyData.rows[index]?.options?.dynamicPrice;
        submitData.name = subCompanyData.rows[index]?.name;
        submitData._id = subCompanyData.rows[index]?._id;
        const params = {
            subCompanyId: submitData._id,
            options: {
                isActive: submitData.isActive,
                firstPrice: submitData.firstPrice,
                minimumPrice: submitData.minimumPrice,
                discountRate: submitData.discountRate
            }
        };
        const result = await stores.companyStore.updateSubCompanyDynamicPriceOptions(params);
        if (result) {
            setSubCompanyRecall(!subCompanyRecallRef.current);
        }
        setIsSubCompanySubmitting(false);
    };

    return (
        <MergeOptionsWrapper data-testid="dynamicPriceOptionsScreen">
            <h2>{companyData?.data?.companyName}</h2>
            <CompanyContanier>
                <Form
                    autoComplete="off"
                    data-testid="dynamicPriceOptionsForm"
                    onFinish={handleSubmit}
                    onChange={e => setIsCompanyFormChanged(true)}
                >
                    <ErrorFocus formik={formik} />
                    <FormikProvider value={formik}>
                    <MergeOptionsTableWrap>
                    <Table
                        loading={companyLoading}
                        dataSource={[companyData.data]}
                        rowKey="_id"
                        data-testid="dynamicPriceOptionsTable"
                        pagination={false}
                        locale={{
                            emptyText: <EmptyMessage />
                        }}
                    >
                            <Column
                                title={`${t(
                                    "dynamicPriceOptions.fields.dynamicPriceOptions.label",
                                    {ns: "dynamicPriceOptions"}
                                )} (${t("dynamicPriceOptions.fields.dynamicPriceOptions.short", {
                                    ns: "dynamicPriceOptions"
                                })})`}
                                dataIndex="isActive"
                                render={text => (
                                    <Form.Item>
                                        <Switch
                                            checked={values.options.dynamicPrice?.isActive}
                                            checkedChildren={t("other.active", {ns: "common"})}
                                            unCheckedChildren={t("other.passive", {ns: "common"})}
                                            data-testid="dynamicPriceOptionSwitch"
                                            onChange={e => {
                                                setFieldValue("options.dynamicPrice.isActive", e);
                                                setIsCompanyFormChanged(true);
                                            }}
                                        />
                                    </Form.Item>
                                )}
                                align="left"
                            />
                            <Column
                                title={t("dynamicPriceOptions.fields.startingPrice", {
                                    ns: "dynamicPriceOptions"
                                })}
                                dataIndex="firstPrice"
                                render={text => (
                                    <Form.Item
                                        validateStatus={
                                            getIn(errors, `options.dynamicPrice.firstPrice`) &&
                                            getIn(touched, `options.dynamicPrice.firstPrice`)
                                                ? "error"
                                                : "success"
                                        }
                                        help={
                                            getIn(errors, `options.dynamicPrice.firstPrice`) &&
                                            getIn(touched, `options.dynamicPrice.firstPrice`)
                                                ? getIn(errors, `options.dynamicPrice.firstPrice`)
                                                : null
                                        }
                                    >
                                        <Input
                                            type="number"
                                            name="firstPrice"
                                            data-testid="firstPriceInput"
                                            value={
                                                values.options?.dynamicPrice?.isActive
                                                    ? values.options?.dynamicPrice?.firstPrice
                                                    : companyData?.data?.options?.dynamicPrice
                                                          ?.firstPrice
                                            }
                                            onChange={e => {
                                                setFieldValue(
                                                    "options.dynamicPrice.firstPrice",
                                                    e.target.value || ""
                                                );
                                            }}
                                            disabled={!values.options?.dynamicPrice?.isActive}
                                        />
                                    </Form.Item>
                                )}
                                align="center"
                            />
                            <Column
                                title={t("dynamicPriceOptions.fields.minimumPrice", {
                                    ns: "dynamicPriceOptions"
                                })}
                                dataIndex="minimumPrice"
                                render={text => (
                                    <Form.Item
                                        validateStatus={
                                            getIn(errors, `options.dynamicPrice.minimumPrice`) &&
                                            getIn(touched, `options.dynamicPrice.minimumPrice`)
                                                ? "error"
                                                : "success"
                                        }
                                        help={
                                            getIn(errors, `options.dynamicPrice.minimumPrice`) &&
                                            getIn(touched, `options.dynamicPrice.minimumPrice`)
                                                ? getIn(errors, `options.dynamicPrice.minimumPrice`)
                                                : null
                                        }
                                    >
                                        <Input
                                            type="number"
                                            name="minimumPrice"
                                            data-testid="minimumPriceInput"
                                            value={
                                                values.options?.dynamicPrice?.isActive
                                                    ? values.options?.dynamicPrice?.minimumPrice
                                                    : companyData?.data?.options?.dynamicPrice
                                                          ?.minimumPrice
                                            }
                                            onChange={e => {
                                                setFieldValue(
                                                    "options.dynamicPrice.minimumPrice",
                                                    e.target.value || ""
                                                );
                                            }}
                                            disabled={!values.options?.dynamicPrice?.isActive}
                                        />
                                    </Form.Item>
                                )}
                                align="center"
                            />
                            <Column
                                title={`${t("dynamicPriceOptions.fields.discountRate", {
                                    ns: "dynamicPriceOptions"
                                })} (%)`}
                                dataIndex="discountRate"
                                render={text => (
                                    <Form.Item
                                        validateStatus={
                                            getIn(errors, `options.dynamicPrice.discountRate`) &&
                                            getIn(touched, `options.dynamicPrice.discountRate`)
                                                ? "error"
                                                : "success"
                                        }
                                        help={
                                            getIn(errors, `options.dynamicPrice.discountRate`) &&
                                            getIn(touched, `options.dynamicPrice.discountRate`)
                                                ? getIn(errors, `options.dynamicPrice.discountRate`)
                                                : null
                                        }
                                    >
                                        <Input
                                            type="number"
                                            name="discountRate"
                                            data-testid="discountRateInput"
                                            value={
                                                values.options?.dynamicPrice?.isActive
                                                    ? values.options?.dynamicPrice?.discountRate
                                                    : companyData?.data?.options?.dynamicPrice
                                                          ?.discountRate
                                            }
                                            onChange={e => {
                                                setFieldValue(
                                                    "options.dynamicPrice.discountRate",
                                                    e.target.value || ""
                                                );
                                            }}
                                            disabled={!values.options?.dynamicPrice?.isActive}
                                        />
                                    </Form.Item>
                                )}
                                align="center"
                            />
                    </Table>
                    </MergeOptionsTableWrap>

                    <MergeOptionsList>
                        <MergeOptionsListRow>
                            <span className="merge-options-label">
                                {t("dynamicPriceOptions.fields.dynamicPriceOptions.label", {
                                    ns: "dynamicPriceOptions"
                                })}{" "}
                                ({t("dynamicPriceOptions.fields.dynamicPriceOptions.short", {ns: "dynamicPriceOptions"})})
                            </span>
                            <Form.Item>
                                <Switch
                                    checked={values.options.dynamicPrice?.isActive}
                                    checkedChildren={t("other.active", {ns: "common"})}
                                    unCheckedChildren={t("other.passive", {ns: "common"})}
                                    onChange={e => {
                                        setFieldValue("options.dynamicPrice.isActive", e);
                                        setIsCompanyFormChanged(true);
                                    }}
                                />
                            </Form.Item>
                        </MergeOptionsListRow>
                        <MergeOptionsListRow>
                            <span className="merge-options-label">
                                {t("dynamicPriceOptions.fields.startingPrice", {ns: "dynamicPriceOptions"})}
                            </span>
                            <Form.Item
                                validateStatus={
                                    getIn(errors, "options.dynamicPrice.firstPrice") &&
                                    getIn(touched, "options.dynamicPrice.firstPrice")
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    getIn(errors, "options.dynamicPrice.firstPrice") &&
                                    getIn(touched, "options.dynamicPrice.firstPrice")
                                        ? getIn(errors, "options.dynamicPrice.firstPrice")
                                        : null
                                }
                            >
                                <Input
                                    type="number"
                                    value={
                                        values.options?.dynamicPrice?.isActive
                                            ? values.options?.dynamicPrice?.firstPrice
                                            : companyData?.data?.options?.dynamicPrice?.firstPrice
                                    }
                                    onChange={e =>
                                        setFieldValue("options.dynamicPrice.firstPrice", e.target.value || "")
                                    }
                                    disabled={!values.options?.dynamicPrice?.isActive}
                                />
                            </Form.Item>
                        </MergeOptionsListRow>
                        <MergeOptionsListRow>
                            <span className="merge-options-label">
                                {t("dynamicPriceOptions.fields.minimumPrice", {ns: "dynamicPriceOptions"})}
                            </span>
                            <Form.Item
                                validateStatus={
                                    getIn(errors, "options.dynamicPrice.minimumPrice") &&
                                    getIn(touched, "options.dynamicPrice.minimumPrice")
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    getIn(errors, "options.dynamicPrice.minimumPrice") &&
                                    getIn(touched, "options.dynamicPrice.minimumPrice")
                                        ? getIn(errors, "options.dynamicPrice.minimumPrice")
                                        : null
                                }
                            >
                                <Input
                                    type="number"
                                    value={
                                        values.options?.dynamicPrice?.isActive
                                            ? values.options?.dynamicPrice?.minimumPrice
                                            : companyData?.data?.options?.dynamicPrice?.minimumPrice
                                    }
                                    onChange={e =>
                                        setFieldValue("options.dynamicPrice.minimumPrice", e.target.value || "")
                                    }
                                    disabled={!values.options?.dynamicPrice?.isActive}
                                />
                            </Form.Item>
                        </MergeOptionsListRow>
                        <MergeOptionsListRow>
                            <span className="merge-options-label">
                                {t("dynamicPriceOptions.fields.discountRate", {ns: "dynamicPriceOptions"})} (%)
                            </span>
                            <Form.Item
                                validateStatus={
                                    getIn(errors, "options.dynamicPrice.discountRate") &&
                                    getIn(touched, "options.dynamicPrice.discountRate")
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    getIn(errors, "options.dynamicPrice.discountRate") &&
                                    getIn(touched, "options.dynamicPrice.discountRate")
                                        ? getIn(errors, "options.dynamicPrice.discountRate")
                                        : null
                                }
                            >
                                <Input
                                    type="number"
                                    value={
                                        values.options?.dynamicPrice?.isActive
                                            ? values.options?.dynamicPrice?.discountRate
                                            : companyData?.data?.options?.dynamicPrice?.discountRate
                                    }
                                    onChange={e =>
                                        setFieldValue("options.dynamicPrice.discountRate", e.target.value || "")
                                    }
                                    disabled={!values.options?.dynamicPrice?.isActive}
                                />
                            </Form.Item>
                        </MergeOptionsListRow>
                    </MergeOptionsList>
                    </FormikProvider>
                    <CompanyButtonContainer justify="end" align="middle">
                        <Checkbox
                            checked={values?.applyDynamicPriceOptions}
                            onChange={e =>
                                setFieldValue("applyDynamicPriceOptions", e.target.checked)
                            }
                        >
                            {handleCheckBoxText(searchText, city)}
                        </Checkbox>
                        <Button
                            htmlType="submit"
                            disabled={!isCompanyFormChanged || isSubmitting}
                            type="primary"
                        >
                            {t("actions.save", {ns: "common"})}
                        </Button>
                    </CompanyButtonContainer>
                </Form>
            </CompanyContanier>
            <FilterWrapper>
                <Col span={12}>
                    <FilterContainer type="flex" gutter={[20, 20]}>
                        <Col span={6}>
                            <CityFilter
                                value={city}
                                onChange={value => {
                                    setCurrentPage(1);
                                    setCity(value);
                                }}
                            />
                        </Col>
                        <Col span={6}>
                            <h5>{t("filters.search", {ns: "common"})}</h5>
                            <Input.Search
                                value={text}
                                data-testid="searchInput"
                                onSearch={value => {
                                    setCurrentPage(1);
                                    setSearchText(value);
                                }}
                                onChange={event => {
                                    setText(event.target.value);
                                }}
                            />
                        </Col>
                    </FilterContainer>
                </Col>
            </FilterWrapper>
            <SubCompanyContainer>
                <SubCompanyTableWrap>
                <Table
                    rowKey="_id"
                    data-testid="subCompaniesTable"
                    dataSource={subCompanyData?.rows}
                    loading={subCompanyLoading}
                    pagination={{
                        total: subCompanyData.dataCount,
                        current: currentPage,
                        onChange: async page => {
                            setCurrentPage(page);
                            history.push(
                                "/companies/dynamic-price-options/" + companyId + "?page=" + page
                            );
                        },
                        hideOnSinglePage: true,
                        showSizeChanger: false
                    }}
                    locale={{
                        emptyText: <EmptyMessage />
                    }}
                >
                    <Column
                        dataIndex="name"
                        title={t("fields.subcompany", {ns: "common"})}
                        render={text => <span data-testid="name">{text}</span>}
                    />
                    <Column
                        dataIndex="city"
                        title={t("fields.city", {ns: "common"})}
                        render={text => <span data-testid="city">{text}</span>}
                    />
                    <Column
                        dataIndex="isActive"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title="DFS"
                        render={(text, record, index) => {
                            return (
                                <Switch
                                    data-testid="dynamicPriceOptionSwitch"
                                    checked={
                                        subCompanyData.rows[index]?.options?.dynamicPrice?.isActive
                                    }
                                    onChange={e => {
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            const options = {isActive: e};
                                            newItem.options.dynamicPrice = options;
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="firstPrice"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title={t("dynamicPriceOptions.fields.startingPrice", {
                            ns: "dynamicPriceOptions"
                        })}
                        render={(text, record, index) => {
                            return (
                                <Input
                                    disabled={
                                        !subCompanyData.rows[index]?.options?.dynamicPrice?.isActive
                                    }
                                    type="number"
                                    name="firstPrice"
                                    data-testid="firstPrice"
                                    value={
                                        subCompanyData.rows[index]?.options?.dynamicPrice
                                            ?.firstPrice
                                    }
                                    onChange={e => {
                                        e.persist();
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            newItem.options.dynamicPrice.firstPrice =
                                                e.target?.value || "";
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="minimumPrice"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title={t("dynamicPriceOptions.fields.minimumPrice", {
                            ns: "dynamicPriceOptions"
                        })}
                        render={(text, record, index) => {
                            return (
                                <Input
                                    disabled={
                                        !subCompanyData.rows[index]?.options?.dynamicPrice?.isActive
                                    }
                                    type="number"
                                    name="minimumPrice"
                                    data-testid="minimumPrice"
                                    value={
                                        subCompanyData.rows[index]?.options?.dynamicPrice
                                            ?.minimumPrice
                                    }
                                    onChange={e => {
                                        e.persist();
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            newItem.options.dynamicPrice.minimumPrice =
                                                e.target?.value || "";
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="discountRate"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title={`${t("dynamicPriceOptions.fields.discountRate", {
                            ns: "dynamicPriceOptions"
                        })} (%)`}
                        render={(text, record, index) => {
                            return (
                                <Input
                                    disabled={
                                        !subCompanyData.rows[index]?.options?.dynamicPrice?.isActive
                                    }
                                    type="number"
                                    name="discountRate"
                                    data-testid="discountRate"
                                    value={
                                        subCompanyData.rows[index]?.options?.dynamicPrice
                                            ?.discountRate
                                    }
                                    onChange={e => {
                                        e.persist();
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            newItem.options.dynamicPrice.discountRate =
                                                e.target?.value || "";
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="_id"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        render={(text, record, index) =>
                            showSubmitButton[text] && (
                                <Button
                                    disabled={isSubCompanySubmitting}
                                    onClick={() => onSubCompanySubmit(index)}
                                    type="primary"
                                    data-testid="submitButton"
                                >
                                    {t("actions.save", {ns: "common"})}
                                </Button>
                            )
                        }
                    />
                </Table>
                </SubCompanyTableWrap>

                <SubCompanyCardList>
                    {subCompanyLoading ? (
                        <div style={{textAlign: "center", padding: 24}}>
                            <Spin size="large" />
                        </div>
                    ) : !subCompanyData?.rows?.length ? (
                        <EmptyMessage />
                    ) : (
                        <>
                            {(subCompanyData.rows || []).map((record, index) => {
                                const dp = record?.options?.dynamicPrice || {};
                                const updateRow = (field, value) => {
                                    setShowSubmitButton(prev => ({...prev, [record._id]: true}));
                                    setSubCompanyData(prevData => {
                                        const newData = {...prevData};
                                        const newItem = {...newData.rows[index]};
                                        if (!newItem.options) newItem.options = {};
                                        if (!newItem.options.dynamicPrice)
                                            newItem.options.dynamicPrice = {};
                                        newItem.options.dynamicPrice[field] = value;
                                        newData.rows[index] = newItem;
                                        return newData;
                                    });
                                };
                                return (
                                    <SubCompanyCard key={record._id}>
                                        <SubCompanyCardTitle>
                                            {record.name} · {record.city}
                                        </SubCompanyCardTitle>
                                        <SubCompanyCardRow>
                                            <span className="subcompany-card-label">
                                                {t("dynamicPriceOptions.fields.dynamicPriceOptions.short", {
                                                    ns: "dynamicPriceOptions"
                                                })}
                                            </span>
                                            <Switch
                                                checked={dp.isActive}
                                                checkedChildren={t("other.active", {ns: "common"})}
                                                unCheckedChildren={t("other.passive", {ns: "common"})}
                                                onChange={e => updateRow("isActive", e)}
                                            />
                                        </SubCompanyCardRow>
                                        <SubCompanyCardRow>
                                            <span className="subcompany-card-label">
                                                {t("dynamicPriceOptions.fields.startingPrice", {
                                                    ns: "dynamicPriceOptions"
                                                })}
                                            </span>
                                            <Input
                                                type="number"
                                                disabled={!dp.isActive}
                                                value={dp.firstPrice}
                                                onChange={e => updateRow("firstPrice", e.target?.value ?? "")}
                                            />
                                        </SubCompanyCardRow>
                                        <SubCompanyCardRow>
                                            <span className="subcompany-card-label">
                                                {t("dynamicPriceOptions.fields.minimumPrice", {
                                                    ns: "dynamicPriceOptions"
                                                })}
                                            </span>
                                            <Input
                                                type="number"
                                                disabled={!dp.isActive}
                                                value={dp.minimumPrice}
                                                onChange={e => updateRow("minimumPrice", e.target?.value ?? "")}
                                            />
                                        </SubCompanyCardRow>
                                        <SubCompanyCardRow>
                                            <span className="subcompany-card-label">
                                                {t("dynamicPriceOptions.fields.discountRate", {
                                                    ns: "dynamicPriceOptions"
                                                })} (%)
                                            </span>
                                            <Input
                                                type="number"
                                                disabled={!dp.isActive}
                                                value={dp.discountRate}
                                                onChange={e => updateRow("discountRate", e.target?.value ?? "")}
                                            />
                                        </SubCompanyCardRow>
                                        <SubCompanyCardActions>
                                            {showSubmitButton[record._id] && (
                                                <Button
                                                    disabled={isSubCompanySubmitting}
                                                    onClick={() => onSubCompanySubmit(index)}
                                                    type="primary"
                                                    block
                                                >
                                                    {t("actions.save", {ns: "common"})}
                                                </Button>
                                            )}
                                        </SubCompanyCardActions>
                                    </SubCompanyCard>
                                );
                            })}
                            {subCompanyData?.dataCount > 10 && (
                                <div style={{marginTop: 16, display: "flex", justifyContent: "center"}}>
                                    <Pagination
                                        total={subCompanyData.dataCount}
                                        current={currentPage}
                                        onChange={page => {
                                            setCurrentPage(page);
                                            history.push(
                                                "/companies/dynamic-price-options/" + companyId + "?page=" + page
                                            );
                                        }}
                                        hideOnSinglePage
                                        showSizeChanger={false}
                                        pageSize={10}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </SubCompanyCardList>
            </SubCompanyContainer>
        </MergeOptionsWrapper>
    );
};
